import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { DocumentLinkComponent } from '@zaoblako/web/shared/components/document-link/document-link.component';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { CardInlineMutationSkeletonV2Component } from '@zaoblako/web/shared/components/card-layout/card-inline-mutation-skeleton-v2/card-inline-mutation-skeleton-v2.component';
import { buildEavAttributeMutationPath, isCardMutationPathPending } from '@zaoblako/web/shared/components/card-layout/universal-card-v2/card-v2-mutation-path.util';
import { ChangeFieldInCardComponent } from '@zaoblako/web/shared/dumbs/change-field-in-card/change-field-in-card.component';
import { CostCenterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-multi-select/cost-center-multi-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { FilialMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-multi-select/filial-multi-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { RoomSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-single-select/room-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';

import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import moment from 'moment-timezone';

@Component({
  selector: 'crm-hd-eav-fields',
  standalone: true,
  templateUrl: './hd-eav-fields.component.html',
  styleUrls: ['./hd-eav-fields.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    DividerModule,
    CheckboxModule,
    InputNumberModule,
    CardInlineMutationSkeletonV2Component,
    FileUploadModule,
    ChangeFieldInCardComponent,
    CostCenterMultiSelectComponent,
    CostCenterSingleSelectComponent,
    DatepickerComponent,
    DaterangepickerComponent,
    DocumentLinkComponent,
    FilialMultiSelectComponent,
    FilialSingleSelectComponent,
    RoomMultiSelectComponent,
    RoomSingleSelectComponent,
    InputTextComponent,
    FilesListComponent,
    JoinPipe,
    OrderByPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HdEavFieldsComponent implements OnChanges {
  @Input() public attributes: EavAttributeFindInterface[] = [];
  @Input() public values: Record<number, any> = {};
  @Input() public mode: 'form' | 'card' = 'form';
  @Input() public canEdit = true;
  @Input() public errors?: any;
  @Input() public hdTaskStatusId?: number;
  @Input() public showRequiredMarker = true;
  @Input() public excludedAttributeTypes: EavAttributeValueTypeEnum[] = [];
  @Input() public mutationLoadingPaths: ReadonlySet<string> = new Set();

  @Output() public readonly fieldSave = new EventEmitter<number>();

  @ViewChild('fileUpload') public fileUpload?: FileUpload;

  @HostBinding('class.hd-eav-fields--form')
  public get isFormMode(): boolean {
    return this.mode === 'form';
  }

  public visibleAttributes: EavAttributeFindInterface[] = [];
  public filesLoading = false;
  public period: { from: Date | null; to: Date | null; } = { from: null, to: null };

  public get EavAttributeValueTypeEnum() {
    return EavAttributeValueTypeEnum;
  }

  public constructor(
    private readonly fileService: FileService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.attributes || changes.excludedAttributeTypes) {
      this.visibleAttributes = (this.attributes || []).filter(
        ({ type }) => !this.excludedAttributeTypes.includes(type)
      );
    }
    if (changes.values) {
      this.syncPeriodFromValues();
    }
  }

  public emitFieldSave = (attributeId: number): void => {
    this.fieldSave.emit(attributeId);
  };

  public isAttributeMutationLoading = (attributeId: number): boolean =>
    isCardMutationPathPending(this.mutationLoadingPaths, buildEavAttributeMutationPath(attributeId));

  public isRequiredField(attribute: EavAttributeFindInterface): boolean {
    if (!this.showRequiredMarker || !this.hdTaskStatusId) {
      return false;
    }

    return (
      attribute.type !== EavAttributeValueTypeEnum.COMPUTED &&
      !!attribute.requiredOnHdTaskStatuses?.map(({ id }) => id).includes(this.hdTaskStatusId)
    );
  }

  public uploadFiles(data: FileUploadHandlerEvent, attribute: EavAttributeFindInterface): void {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.values[attribute.id] = this.values[attribute.id]?.length
          ? this.values[attribute.id].concat(files)
          : files;
        this.fileUpload?.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: FileFindInterface[], file: FileFindInterface): void {
    const index = files.indexOf(file);
    if (index >= 0) {
      files.splice(index, 1);
      this.cdRef.markForCheck();
    }
  }

  public setDates(dates: [Date, Date], attribute: EavAttributeFindInterface): void {
    this.values[attribute.id] = {
      from: moment(dates[0]).format('YYYY-MM-DD'),
      to: moment(dates[1]).format('YYYY-MM-DD')
    };
    this.period = {
      from: dates[0],
      to: dates[1]
    };
  }

  public toFile(file: unknown): FileFindInterface {
    return file as FileFindInterface;
  }

  private syncPeriodFromValues(): void {
    this.period = { from: null, to: null };
    if (!this.values) {
      return;
    }

    Object.values(this.values).forEach((value) => {
      if (typeof value === 'object' && value !== null) {
        if ('from' in value && value.from) {
          this.period.from = moment(value.from).toDate();
        }
        if ('to' in value && value.to) {
          this.period.to = moment(value.to).toDate();
        }
      }
    });
  }
}
